import { EVENTS } from '@/lib/consts'
import { useCore } from '@/composables/core'
import { useLogger } from '@booksy-com/vue-tools'
import { useEmitter } from '@/composables/emitter'
import { useReady } from '@/composables/ready'

import { PUSH_TYPES, PUSH_REDIRECTS, PUSH_REDIRECT_ROUTES } from './consts'
import { path } from 'ramda'
import { notificationAction } from '@/lib/notifications'
import { useDefaultRedirect } from '@/composables/default-redirect'

const logger = useLogger('push-notifications')

type PushData = { type?: string; args?: unknown[] }

type OpenPushPayload = {
  type: string
  data?: PushData
}

const _redirect = ({ target_type }: { target_type: string }): Promise<void> => {
  const { router, store } = useCore()
  const defaultRedirect = useDefaultRedirect()

  return new Promise((resolve) => {
    const redirection = PUSH_REDIRECTS[target_type as keyof typeof PUSH_REDIRECTS]
    const routeName = path(['currentRoute', 'value', 'name'], router)

    if (redirection === PUSH_REDIRECT_ROUTES.DEFAULT) {
      ;(store.dispatch as (action: string) => Promise<unknown>)('business/reloadBusiness')
        .then(() => defaultRedirect())
        .finally(resolve)
    } else if (redirection === PUSH_REDIRECT_ROUTES.CALENDAR && routeName !== PUSH_REDIRECT_ROUTES.CALENDAR) {
      ;(router.push as (loc: { name: string }) => Promise<unknown>)({ name: 'calendar' }).then(resolve)
    } else {
      resolve()
    }
  })
}

const _openDefaultPush = ({ type, data }: OpenPushPayload): Promise<void> | undefined => {
  logger.log('openDefaultPush started', { type, data })

  const target_type = path(['type'], data) as string | undefined
  const target_id = path(['args', '0'], data)

  if (target_type === undefined) {
    return undefined
  }

  const payload = {
    target_type,
    target_id,
    params: {},
  }

  return _redirect({ target_type })
    .then(() => {
      return notificationAction({
        ...payload,
      })
    })
    .catch((e: unknown) => {
      logger.error(e)
    })
    .finally(() => {
      logger.log('openDefaultPush settled', { type, data })
    })
}

const openPush = (payload: OpenPushPayload): Promise<void> | undefined => {
  if (payload.type === PUSH_TYPES.DEFAULT) {
    return _openDefaultPush(payload)
  }
  logger.error(`push type: ${payload.type} is not recognized`)
  return undefined
}

const init = (): void => {
  const emitter = useEmitter()

  emitter.on(EVENTS.WEB_VIEW_PUSH_OPEN, (data: PushData) => {
    useReady().whenReady.then(() => {
      logger.log(EVENTS.WEB_VIEW_PUSH_OPEN, data)
      return openPush({
        type: PUSH_TYPES.DEFAULT,
        data,
      })
    })
  })
}

export type UsePushNotificationsReturn = {
  init: () => void
  openPush: (payload: OpenPushPayload) => Promise<void> | undefined
  PUSH_TYPES: typeof PUSH_TYPES
}

export function usePushNotifications (): UsePushNotificationsReturn {
  return {
    init,
    openPush,
    PUSH_TYPES,
  }
}
